import { cn } from '@neo/lib/utils'
import { Popover, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import { chain } from 'lodash'
import { Braces, CircleEllipsis, Hash } from 'lucide-react'
import Quill, { type EmitterSource } from 'quill'
import type Delta from 'quill-delta'
import type React from 'react'
import { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useState } from 'react'
import { VariableListPopover } from 'v2/shared/components/variable-list-popover'
import { FlowTypes } from 'v2/types'
import { logger } from 'v2/utils/logger'
import { FlowNodeIconClass } from '../config/panel.config'
import { FlowTriggerConfigMap, nodeIdRegex, VarGroups } from '../constants'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useStartNodeType } from '../hooks/use-start-node-type'
import { FlowNodeTypes } from '../types'
import {
  escapeIdSelector,
  insertNodeVarAtCursorUtil,
  isVariableInput,
} from '../utils/nodevar-utils'

interface NodevarListProps {
  nodevars?: string[]
  onSelect?: (nodevar: string) => void
  className?: string
  /**
   * Selector for the input element that the node variable should be inserted into
   * This is required to insert the node variable at the cursor position automatically
   * Important: This text should be unique to the input element
   * @example
   * Eg: 'textarea[name="prompt"]'
   * Eg: 'input[name="ivr_input"]'
   */
  inputElementSelector?: string
  /**
   * If true, the selected nodevar will replace the entire field content
   * If false (default), the nodevar will be inserted at the cursor position
   */
  replaceEntireField?: boolean
  sideOffset?: number
}

//  Handle node variable trigger detection
const useNodeVarTrigger = (inputElementSelector?: string, onTrigger?: () => void) => {
  useEffect(() => {
    if (!inputElementSelector || !onTrigger) {
      return
    }

    const escapedSelector = escapeIdSelector(inputElementSelector)
    const element = document.querySelector(escapedSelector)
    if (!element) return

    // Try to get Quill instance first
    const found = Quill.find(element)
    const quill = found instanceof Quill ? found : null

    const handleQuillTextChange = (_delta: Delta, _oldDelta: Delta, source: EmitterSource) => {
      if (source !== 'user' || !quill) return
      const text = quill.getText()

      if (isVariableInput(text)) {
        onTrigger()
      }
    }

    const handleInputChange = (e: Event) => {
      const target = e.target as HTMLInputElement

      if (isVariableInput(target.value)) {
        onTrigger()
      }
    }

    if (quill) {
      quill.on('text-change', handleQuillTextChange)
    } else {
      element.addEventListener('input', handleInputChange)
    }

    return () => {
      if (quill) {
        quill.off('text-change', handleQuillTextChange)
      } else {
        element.removeEventListener('input', handleInputChange)
      }
    }
  }, [inputElementSelector, onTrigger])
}

// Error Boundary component to catch React Flow context errors
class NodevarListErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    // Check if the error is related to React Flow context
    if (error.message.includes('zustand provider') || error.message.includes('React Flow')) {
      return { hasError: true }
    }
    return { hasError: false }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.warn('NodevarList: React Flow context not available, using fallback:', {
      error: error.message,
      errorInfo,
    })
  }

  render() {
    const { hasError } = this.state
    const { fallback, children } = this.props
    if (hasError) return fallback
    return children
  }
}

// Simple component for queue context without React Flow
const NodevarListSimple: React.FC<NodevarListProps> = (props) => {
  const { nodevars, inputElementSelector, replaceEntireField, sideOffset, onSelect, className } =
    props
  const [open, setOpen] = useState(false)

  const groups = useMemo(() => {
    if (!nodevars) return []

    // For queue context, just group all variables under "Available Variables"
    return [
      {
        id: 'available-variables',
        name: 'Available Variables',
        variables: nodevars.map((v) => ({
          key: v,
          label: v.replace(/^\{\{|\}\}$/g, ''), // Remove {{ and }} for display
        })),
        isStartNode: false,
      },
    ]
  }, [nodevars])

  // Listen to input changes and open popover if '{{' is typed
  useNodeVarTrigger(inputElementSelector, () => setOpen(true))

  const insertNodeVarAtCursor = (nodevar: string) => {
    insertNodeVarAtCursorUtil(nodevar, inputElementSelector, replaceEntireField, onSelect, () =>
      setOpen(false),
    )
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip content="Insert node variables">
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn('text-tertiary pointer', open && 'text-primary')}
              aria-label="Insert node variables"
            >
              <Braces size={14} />
            </button>
          </PopoverTrigger>
        </Tooltip>
        <VariableListPopover
          mode="simple"
          title="Insert Node Variable"
          groups={groups}
          onSelect={insertNodeVarAtCursor}
          onClose={() => setOpen(false)}
          emptyStateTitle="No variables found"
          emptyStateDescription="Connect the component with other nodes to populate."
          sideOffset={sideOffset}
        />
      </Popover>
    </div>
  )
}

// Original component with React Flow hooks
const NodevarListWithReactFlow: React.FC<NodevarListProps> = (props) => {
  const {
    nodevars,
    inputElementSelector,
    replaceEntireField,
    sideOffset = 470,
    onSelect,
    className,
  } = props
  const [open, setOpen] = useState(false)
  const startNodeTypeRaw = useStartNodeType()
  const startNodeType =
    (startNodeTypeRaw === 'omni' ? FlowTypes.incoming_call : startNodeTypeRaw) ||
    FlowTypes.incoming_call
  const { getNodes, getNode } = useTypedReactFlow()

  const groups = useMemo(() => {
    if (!nodevars) return []

    // Get all nodes from the flow and create lookup maps for efficiency
    const nodes = getNodes() || []
    const nodesByName = new Map<string, (typeof nodes)[0]>()
    const nodesById = new Map<string, (typeof nodes)[0]>()

    // Build lookup maps once
    nodes.forEach((node) => {
      const nodeData = node.data
      const metaName = nodeData?.meta?.name
      const configName = nodeData?.config?.name

      nodesById.set(node.id, node)
      if (metaName) nodesByName.set(metaName, node)
      if (configName) nodesByName.set(configName, node)
    })

    // Deduplicate variables first
    const uniqueNodevars = [...new Set(nodevars)]
    const groupedVars = chain(uniqueNodevars)
      .groupBy((variable) => {
        // Check for static boolean values first
        if (variable === 'true' || variable === 'false') {
          return VarGroups.STATIC_VALUES
        }

        // Extract node name from the variable
        const match = variable.match(nodeIdRegex)
        if (!match) return VarGroups.DEFAULT_SENDER_IDS

        // Group by the extracted name directly - this ensures unique groups
        // regardless of whether multiple names reference the same node
        return match[1]
      })
      .value()
    return Object.entries(groupedVars)
      .map(([groupName, vars]) => {
        // Find the node that matches this group name (for display purposes)
        const node = nodesByName.get(groupName)
        const nodeData = node?.data
        const metaNameLower = nodeData?.meta?.name?.toLowerCase()
        const isStartNode = metaNameLower === FlowNodeTypes.start
        const isDefaultSenderIds = groupName === VarGroups.DEFAULT_SENDER_IDS
        const isStaticValues = groupName === VarGroups.STATIC_VALUES

        return {
          id: node?.id || groupName,
          name: (() => {
            if (isStaticValues) return 'Static values'
            if (isDefaultSenderIds) return 'Sender IDs'
            if (isStartNode) return FlowTriggerConfigMap[startNodeType]?.title ?? 'Start'
            // Use the group name directly instead of node's config name
            return groupName
          })(),
          variables: vars.map((v) => ({
            key: v,
            label: v.replace(/^\{\{|\}\}$/g, ''), // Remove {{ and }} for display
          })),
          isStartNode,
        }
      })
      .sort((a, b) => {
        const getPriority = (item: typeof a) => {
          if (item.id === VarGroups.DEFAULT_SENDER_IDS) return 0 // Sender IDs first
          if (item.id === VarGroups.STATIC_VALUES) return 1 // Static values second
          if (item.isStartNode) return 2 // Start nodes third
          return 3 // Others last
        }

        const priorityDiff = getPriority(a) - getPriority(b)
        return priorityDiff !== 0 ? priorityDiff : a.name.localeCompare(b.name)
      })
  }, [nodevars, startNodeType, getNodes])

  const handleGroupIcon = useMemo(
    () =>
      function handleIcon(groupId: string) {
        const createIconWrapper = (icon: React.ReactNode, className: string) => (
          <div className={cn('rounded p-1', className)}>{icon}</div>
        )
        // Static group icons
        if (groupId === VarGroups.STATIC_VALUES) {
          return createIconWrapper(<CircleEllipsis size={16} />, 'bg-bg2 text-secondary')
        }
        if (groupId === VarGroups.DEFAULT_SENDER_IDS) {
          return createIconWrapper(<Hash size={16} />, 'bg-bg2 text-secondary')
        }
        // Node-based icons
        const node = getNode(groupId)
        if (!node) return null

        const nodeData = node.data
        const isStartNode = nodeData?.meta?.name?.toLowerCase() === FlowNodeTypes.start

        if (isStartNode) {
          const config = FlowTriggerConfigMap[startNodeType]
          return config ? createIconWrapper(config.icon, config.class) : null
        }

        const iconConfig = FlowNodeIconClass[node.type as FlowNodeTypes]
        return iconConfig ? createIconWrapper(iconConfig.icon, iconConfig.class) : null
      },
    [startNodeType, getNode],
  )

  // Listen to input changes and open popover if '{{' is typed
  useNodeVarTrigger(inputElementSelector, () => setOpen(true))

  const insertNodeVarAtCursor = (nodevar: string) => {
    insertNodeVarAtCursorUtil(nodevar, inputElementSelector, replaceEntireField, onSelect, () =>
      setOpen(false),
    )
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip content="Insert node variables">
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn('text-tertiary pointer mt-1.5', open && 'text-primary')}
              aria-label="Insert node variables"
            >
              <Braces size={14} />
            </button>
          </PopoverTrigger>
        </Tooltip>
        <VariableListPopover
          mode="simple"
          title="Insert Node Variable"
          groups={groups}
          onSelect={insertNodeVarAtCursor}
          onClose={() => setOpen(false)}
          emptyStateTitle="No variables found"
          emptyStateDescription="Connect the component with other nodes to populate."
          groupIcon={handleGroupIcon}
          sideOffset={sideOffset}
        />
      </Popover>
    </div>
  )
}

// Main component with error boundary
export const NodevarList: React.FC<NodevarListProps> = (props) => (
  <NodevarListErrorBoundary fallback={<NodevarListSimple {...props} />}>
    <NodevarListWithReactFlow {...props} />
  </NodevarListErrorBoundary>
)
